import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, FloppyDisk, CheckCircle, Clock, Printer,
  AirplaneTilt, Anchor, FileText, PencilSimple, Eye,
  CaretDown, CaretUp, WarningOctagon, ListChecks,
  FilePdf, MicrosoftWordLogo, PaperPlaneTilt, XCircle,
  Seal, ArrowCounterClockwise, ChatCircleDots, List
} from '@phosphor-icons/react';

const API = '/api';

const SECTION_LABELS = {
  synopsis: 'Synopsis',
  history_of_flight: 'History of the Flight',
  history_of_voyage: 'History of the Voyage',
  injuries: 'Injuries to Persons',
  casualties: 'Casualties',
  damage_to_aircraft: 'Damage to Aircraft',
  damage_to_vessel: 'Damage to Vessel',
  other_damage: 'Other Damage',
  personnel_information: 'Personnel Information',
  aircraft_information: 'Aircraft Information',
  vessel_information: 'Vessel Information',
  meteorological_information: 'Meteorological Information',
  aids_to_navigation: 'Aids to Navigation',
  communications: 'Communications',
  aerodrome_information: 'Aerodrome Information',
  port_information: 'Port Information',
  flight_recorders: 'Flight Recorders',
  voyage_data_recorder: 'Voyage Data Recorder',
  wreckage_and_impact: 'Wreckage and Impact Information',
  wreckage_and_damage: 'Wreckage and Damage Information',
  medical_and_pathological: 'Medical and Pathological Information',
  fire: 'Fire',
  survival_aspects: 'Survival Aspects',
  tests_and_research: 'Tests and Research',
  organizational_and_management: 'Organizational and Management Information',
  analysis: 'Analysis',
  findings: 'Findings',
  probable_causes: 'Probable Cause(s)',
  contributing_factors: 'Contributing Factors',
  safety_recommendations: 'Safety Recommendations',
};

const SECTION_GROUPS = {
  aviation: [
    { group: '1. Factual Information', keys: ['synopsis', 'history_of_flight', 'injuries', 'damage_to_aircraft', 'other_damage', 'personnel_information', 'aircraft_information', 'meteorological_information', 'aids_to_navigation', 'communications', 'aerodrome_information', 'flight_recorders', 'wreckage_and_impact', 'medical_and_pathological', 'fire', 'survival_aspects', 'tests_and_research', 'organizational_and_management'] },
    { group: '2. Analysis', keys: ['analysis'] },
    { group: '3. Conclusions', keys: ['findings', 'probable_causes', 'contributing_factors'] },
    { group: '4. Safety Recommendations', keys: ['safety_recommendations'] },
  ],
  maritime: [
    { group: '1. Factual Information', keys: ['synopsis', 'history_of_voyage', 'casualties', 'damage_to_vessel', 'other_damage', 'personnel_information', 'vessel_information', 'meteorological_information', 'aids_to_navigation', 'communications', 'port_information', 'voyage_data_recorder', 'wreckage_and_damage', 'medical_and_pathological', 'fire', 'survival_aspects', 'tests_and_research', 'organizational_and_management'] },
    { group: '2. Analysis', keys: ['analysis'] },
    { group: '3. Conclusions', keys: ['findings', 'probable_causes', 'contributing_factors'] },
    { group: '4. Safety Recommendations', keys: ['safety_recommendations'] },
  ],
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', bg: 'bg-slate-200', text: 'text-slate-700' },
  pending_approval: { label: 'Pending IIC Approval', bg: 'bg-amber-100', text: 'text-amber-800' },
  approved: { label: 'IIC Approved', bg: 'bg-emerald-100', text: 'text-emerald-800' },
  rejected: { label: 'Returned by IIC', bg: 'bg-red-100', text: 'text-red-800' },
  review: { label: 'Under Review', bg: 'bg-amber-100', text: 'text-amber-800' },
  final: { label: 'Final', bg: 'bg-emerald-100', text: 'text-emerald-800' },
};

function SectionContent({ value, isListType }) {
  if (!value) return <p className="text-slate-400 italic text-sm">Not yet documented.</p>;
  if (isListType && Array.isArray(value)) {
    return (
      <ol className="list-decimal list-inside space-y-1">
        {value.filter(v => v?.trim()).map((item, i) => (
          <li key={`item-${i}`} className="text-sm text-slate-800 leading-relaxed">{item}</li>
        ))}
      </ol>
    );
  }
  return <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{String(value)}</p>;
}

function TableOfContents({ groups, sections, onScrollTo, activeSection }) {
  const hasContent = (key) => {
    const v = sections[key];
    return v && (typeof v === 'string' ? v.trim() : Array.isArray(v) ? v.filter(i => i?.trim()).length > 0 : true);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sticky top-4" data-testid="table-of-contents">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
        <List size={16} className="text-slate-600" />
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Table of Contents</h3>
      </div>
      <nav className="space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto pr-1">
        {groups.map((g, gi) => (
          <div key={g.group} className="mb-2">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 px-1">{g.group}</p>
            {g.keys.map((key) => {
              const filled = hasContent(key);
              const label = SECTION_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const isActive = activeSection === key;
              return (
                <button
                  key={key}
                  onClick={() => onScrollTo(key)}
                  data-testid={`toc-${key}`}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors ${
                    isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${filled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span className="truncate">{label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>
    </div>
  );
}

function RejectionBanner({ rejectionHistory }) {
  if (!rejectionHistory || rejectionHistory.length === 0) return null;
  const latest = rejectionHistory[rejectionHistory.length - 1];
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4" data-testid="rejection-banner">
      <div className="flex items-start gap-3">
        <ArrowCounterClockwise size={20} className="text-red-600 shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold text-red-800">Report Returned by IIC</h4>
          <p className="text-xs text-red-600 mt-0.5">
            Returned by <strong>{latest.rejected_by_name || latest.rejected_by}</strong> on {new Date(latest.rejected_at).toLocaleString()}
          </p>
          {latest.comments && (
            <div className="mt-2 bg-white rounded-lg p-3 border border-red-100">
              <div className="flex items-center gap-1 mb-1">
                <ChatCircleDots size={12} className="text-red-500" />
                <span className="text-[10px] font-bold text-red-500 uppercase">IIC Comments</span>
              </div>
              <p className="text-sm text-slate-800">{latest.comments}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RejectModal({ open, onClose, onReject }) {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    await onReject(comments);
    setSubmitting(false);
    setComments('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reject-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Return Report</h3>
        <p className="text-sm text-slate-500 mb-4">Provide comments explaining what needs to be revised.</p>
        <Textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Enter your review comments..."
          className="rounded-lg min-h-[120px] mb-4"
          data-testid="reject-comments-input"
        />
        <div className="flex justify-end gap-3">
          <Button variant="outline" className="rounded-lg" onClick={onClose} data-testid="reject-cancel">Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white rounded-lg"
            onClick={handleSubmit}
            disabled={submitting || !comments.trim()}
            data-testid="reject-confirm"
          >
            <XCircle size={16} className="mr-2" />
            {submitting ? 'Returning...' : 'Return Report'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ReportGeneration() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'iic';
  const isIIC = user?.role === 'admin' || user?.role === 'iic';
  const printRef = useRef(null);
  const sectionRefs = useRef({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [data, setData] = useState(null);
  const [sections, setSections] = useState({});
  const [reportStatus, setReportStatus] = useState('draft');
  const [lastSaved, setLastSaved] = useState(null);
  const [mode, setMode] = useState('preview');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showToc, setShowToc] = useState(true);
  const [reportMeta, setReportMeta] = useState({});

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investigations/${id}/report`, { withCredentials: true });
      setData(res.data);
      const merged = { ...res.data.auto_sections };
      if (res.data.report?.sections) {
        Object.entries(res.data.report.sections).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') merged[k] = v;
        });
      }
      setSections(merged);
      setReportStatus(res.data.report?.status || 'draft');
      setLastSaved(res.data.report?.last_saved);
      setReportMeta({
        rejectionHistory: res.data.report?.rejection_history || [],
        submittedBy: res.data.report?.submitted_by_name || res.data.report?.submitted_by,
        submittedAt: res.data.report?.submitted_at,
        approvedBy: res.data.report?.approved_by_name || res.data.report?.approved_by,
        approvedAt: res.data.report?.approved_at,
      });
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (status) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/investigations/${id}/report`, {
        sections,
        status: status || reportStatus,
      }, { withCredentials: true });
      setReportStatus(status || reportStatus);
      setLastSaved(res.data.last_saved);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving report');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!window.confirm('Submit this report to the IIC for approval? The IIC will be notified.')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/investigations/${id}/report/submit-for-approval`, {}, { withCredentials: true });
      setReportStatus(res.data.status);
      setLastSaved(res.data.last_saved);
      alert('Report submitted to IIC for approval.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error submitting report');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this report? This confirms the report content is accurate and complete.')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/investigations/${id}/report/approve`, {}, { withCredentials: true });
      setReportStatus(res.data.status);
      setLastSaved(res.data.last_saved);
      alert('Report approved successfully.');
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error approving report');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async (comments) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/investigations/${id}/report/reject`, { comments }, { withCredentials: true });
      setReportStatus(res.data.status);
      setLastSaved(res.data.last_saved);
      alert('Report returned with comments.');
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error rejecting report');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => { window.print(); };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const res = await axios.get(`${API}/investigations/${id}/report/${format}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data?.investigation?.investigation_number || 'Report'}_Report.${format === 'pdf' ? 'pdf' : 'docx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleSectionChange = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(key);
    }
  };

  const filledCount = useMemo(() => Object.values(sections).filter(v => v && (typeof v === 'string' ? v.trim() : Array.isArray(v) ? v.filter(i => i?.trim()).length > 0 : true)).length, [sections]);

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-slate-500">Compiling report...</div></div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Unable to load report data</div>;

  const { investigation, incident, metadata } = data;
  const sector = investigation?.sector || 'aviation';
  const groups = SECTION_GROUPS[sector] || SECTION_GROUPS.aviation;
  const isListSection = (key) => ['probable_causes', 'contributing_factors', 'safety_recommendations', 'findings'].includes(key);
  const totalSections = (data.sections_template || []).length;
  const statusCfg = STATUS_CONFIG[reportStatus] || STATUS_CONFIG.draft;
  const canEditReport = canEdit && ['draft', 'rejected'].includes(reportStatus);

  return (
    <>
      {/* Screen UI (hidden during print) */}
      <div className="max-w-[1400px] mx-auto print:hidden" data-testid="report-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigate(`/investigations/${id}`)} data-testid="back-to-investigation">
            <ArrowLeft size={16} />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Module 18: Final Report</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
                {sector === 'aviation' ? <AirplaneTilt size={12} className="inline mr-1" /> : <Anchor size={12} className="inline mr-1" />}
                {sector}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`} data-testid="report-status-badge">
                {statusCfg.label}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
              <span className="font-mono">{investigation?.investigation_number}</span>
              <span>{investigation?.title}</span>
              <span>{filledCount}/{totalSections} sections</span>
              {lastSaved && <span>Saved: {new Date(lastSaved).toLocaleString()}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline" size="sm" className="rounded-lg"
              onClick={() => setShowToc(prev => !prev)}
              data-testid="toggle-toc"
            >
              <List size={14} className="mr-1" /> ToC
            </Button>
            <Button
              variant={mode === 'preview' ? 'default' : 'outline'} size="sm" className="rounded-lg"
              onClick={() => setMode('preview')} data-testid="mode-preview"
            >
              <Eye size={14} className="mr-1" /> Preview
            </Button>
            {canEditReport && (
              <Button
                variant={mode === 'edit' ? 'default' : 'outline'} size="sm" className="rounded-lg"
                onClick={() => setMode('edit')} data-testid="mode-edit"
              >
                <PencilSimple size={14} className="mr-1" /> Edit
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-lg" onClick={handlePrint} data-testid="print-report">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button
              variant="outline" size="sm"
              className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => handleDownload('pdf')}
              disabled={downloading === 'pdf'}
              data-testid="download-pdf"
            >
              <FilePdf size={14} className="mr-1" />
              {downloading === 'pdf' ? 'Generating...' : 'PDF'}
            </Button>
            <Button
              variant="outline" size="sm"
              className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => handleDownload('docx')}
              disabled={downloading === 'docx'}
              data-testid="download-docx"
            >
              <MicrosoftWordLogo size={14} className="mr-1" />
              {downloading === 'docx' ? 'Generating...' : 'Word'}
            </Button>
          </div>
        </div>

        {/* Rejection Banner */}
        {reportStatus === 'rejected' && <RejectionBanner rejectionHistory={reportMeta.rejectionHistory} />}

        {/* Approval Info Banner */}
        {reportStatus === 'pending_approval' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="pending-approval-banner">
            <div className="flex items-center gap-3">
              <PaperPlaneTilt size={20} className="text-amber-600" />
              <div>
                <h4 className="text-sm font-semibold text-amber-800">Awaiting IIC Approval</h4>
                <p className="text-xs text-amber-600">
                  Submitted by <strong>{reportMeta.submittedBy}</strong>
                  {reportMeta.submittedAt && ` on ${new Date(reportMeta.submittedAt).toLocaleString()}`}
                </p>
              </div>
              {isIIC && (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" onClick={handleApprove} disabled={saving} data-testid="approve-report-btn">
                    <Seal size={14} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-lg" onClick={() => setShowRejectModal(true)} disabled={saving} data-testid="reject-report-btn">
                    <XCircle size={14} className="mr-1" /> Return
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {reportStatus === 'approved' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4" data-testid="approved-banner">
            <div className="flex items-center gap-3">
              <Seal size={20} className="text-emerald-600" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-800">Report Approved by IIC</h4>
                <p className="text-xs text-emerald-600">
                  Approved by <strong>{reportMeta.approvedBy}</strong>
                  {reportMeta.approvedAt && ` on ${new Date(reportMeta.approvedAt).toLocaleString()}`}
                </p>
              </div>
              {isIIC && (
                <div className="ml-auto">
                  <Button size="sm" className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg" onClick={() => handleSave('final')} disabled={saving} data-testid="finalize-report">
                    <CheckCircle size={14} className="mr-1" /> {saving ? 'Finalizing...' : 'Finalize Report'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Readiness bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1"><ListChecks size={12} className="text-blue-600" /> Tasks: {metadata?.completed_tasks}/{metadata?.total_tasks}</span>
              <span className="flex items-center gap-1"><FileText size={12} className="text-purple-600" /> Findings: {metadata?.specialist_findings_count}</span>
              <span className="flex items-center gap-1">
                {metadata?.synthesis_status === 'completed' ? <CheckCircle size={12} className="text-emerald-600" /> : <Clock size={12} className="text-slate-400" />}
                Synthesis: {metadata?.synthesis_status || 'not started'}
              </span>
            </div>
            <span className="text-xs text-slate-500">{filledCount}/{totalSections} sections documented</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-1.5 rounded-full transition-all" style={{ width: `${totalSections > 0 ? Math.round((filledCount / totalSections) * 100) : 0}%` }} />
          </div>
        </div>

        {/* Main Content with ToC Sidebar */}
        <div className="flex gap-4">
          {/* Table of Contents Sidebar */}
          {showToc && (
            <div className="w-64 shrink-0 hidden lg:block">
              <TableOfContents
                groups={groups}
                sections={sections}
                onScrollTo={scrollToSection}
                activeSection={activeSection}
              />
            </div>
          )}

          {/* Report Content */}
          <div className="flex-1 space-y-4 min-w-0">
            {groups.map((g, gi) => (
              <div key={g.group} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`report-group-${gi}`}>
                <button
                  onClick={() => setCollapsedGroups(prev => ({ ...prev, [gi]: !prev[gi] }))}
                  className="w-full flex items-center justify-between px-5 py-3 bg-slate-800 hover:bg-slate-700 transition-colors text-left"
                  data-testid={`toggle-group-${gi}`}
                >
                  <span className="text-sm font-bold text-white uppercase tracking-wider">{g.group}</span>
                  {collapsedGroups[gi] ? <CaretDown size={16} className="text-slate-400" /> : <CaretUp size={16} className="text-slate-400" />}
                </button>
                {!collapsedGroups[gi] && (
                  <div className="divide-y divide-slate-100">
                    {g.keys.map((sectionKey) => {
                      const label = SECTION_LABELS[sectionKey] || sectionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      const value = sections[sectionKey];
                      const isList = isListSection(sectionKey);
                      const hasContent = value && (typeof value === 'string' ? value.trim() : Array.isArray(value) ? value.filter(i => i?.trim()).length > 0 : true);

                      return (
                        <div
                          key={sectionKey}
                          ref={(el) => { sectionRefs.current[sectionKey] = el; }}
                          className="px-5 py-4 scroll-mt-20"
                          data-testid={`section-${sectionKey}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2 h-2 rounded-full ${hasContent ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{label}</h3>
                          </div>
                          {mode === 'edit' && canEditReport ? (
                            isList ? (
                              <div className="space-y-2">
                                {(Array.isArray(value) ? value : [value || '']).map((item, idx) => (
                                  <div key={`list-${idx}`} className="flex items-start gap-2">
                                    <span className="text-xs font-mono text-slate-400 mt-2.5 w-5 shrink-0">{idx + 1}.</span>
                                    <Textarea
                                      value={item || ''}
                                      onChange={(e) => {
                                        const arr = Array.isArray(value) ? [...value] : [value || ''];
                                        arr[idx] = e.target.value;
                                        handleSectionChange(sectionKey, arr);
                                      }}
                                      className="rounded-lg min-h-[50px] text-sm"
                                      data-testid={`edit-${sectionKey}-${idx}`}
                                    />
                                    {(Array.isArray(value) ? value : [value || '']).length > 1 && (
                                      <button
                                        onClick={() => {
                                          const arr = (Array.isArray(value) ? value : [value || '']).filter((_, i) => i !== idx);
                                          handleSectionChange(sectionKey, arr);
                                        }}
                                        className="text-red-400 hover:text-red-600 mt-2 text-xs shrink-0"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <Button
                                  variant="outline" size="sm" className="rounded-lg text-xs"
                                  onClick={() => handleSectionChange(sectionKey, [...(Array.isArray(value) ? value : [value || '']), ''])}
                                >
                                  + Add Item
                                </Button>
                              </div>
                            ) : (
                              <Textarea
                                value={value || ''}
                                onChange={(e) => handleSectionChange(sectionKey, e.target.value)}
                                className="rounded-lg min-h-[100px] text-sm"
                                placeholder={`Enter ${label.toLowerCase()}...`}
                                data-testid={`edit-${sectionKey}`}
                              />
                            )
                          ) : (
                            <SectionContent value={value} isListType={isList} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Bar */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 mt-6 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 flex items-center justify-between z-10" data-testid="report-action-bar">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock size={14} />
            {lastSaved ? `Saved: ${new Date(lastSaved).toLocaleString()}` : 'Not saved yet'}
          </div>
          <div className="flex gap-3 flex-wrap">
            {/* Draft/Rejected state: can save and submit */}
            {canEditReport && (
              <>
                <Button variant="outline" className="rounded-lg" onClick={() => handleSave('draft')} disabled={saving} data-testid="save-report-draft">
                  <FloppyDisk size={16} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg"
                  onClick={handleSubmitForApproval}
                  disabled={saving}
                  data-testid="send-to-iic-btn"
                >
                  <PaperPlaneTilt size={16} className="mr-2" />
                  {saving ? 'Submitting...' : 'Send to IIC for Approval'}
                </Button>
              </>
            )}

            {/* Pending approval: IIC can approve/reject */}
            {reportStatus === 'pending_approval' && isIIC && (
              <>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" onClick={handleApprove} disabled={saving} data-testid="approve-report-bottom">
                  <Seal size={16} className="mr-2" /> {saving ? 'Approving...' : 'Approve Report'}
                </Button>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-lg" onClick={() => setShowRejectModal(true)} disabled={saving} data-testid="reject-report-bottom">
                  <XCircle size={16} className="mr-2" /> Return with Comments
                </Button>
              </>
            )}

            {/* Approved: IIC can finalize */}
            {reportStatus === 'approved' && isIIC && (
              <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg" onClick={() => handleSave('final')} disabled={saving} data-testid="finalize-report-bottom">
                <CheckCircle size={16} className="mr-2" />
                {saving ? 'Finalizing...' : 'Finalize Report'}
              </Button>
            )}

            {/* Final status */}
            {reportStatus === 'final' && (
              <span className="flex items-center gap-2 text-sm text-emerald-700 font-semibold px-3 py-2 bg-emerald-50 rounded-lg" data-testid="final-status-indicator">
                <CheckCircle size={16} /> Report Finalized
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <RejectModal open={showRejectModal} onClose={() => setShowRejectModal(false)} onReject={handleReject} />

      {/* Print-only view */}
      <div className="hidden print:block" ref={printRef}>
        <style>{`
          @media print {
            @page { margin: 2cm; size: A4; }
            body { font-family: 'Times New Roman', serif; font-size: 12pt; color: #000; }
          }
        `}</style>
        <div style={{ fontFamily: "'Times New Roman', serif" }}>
          {/* Cover header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem', borderBottom: '3px solid #000', paddingBottom: '1.5rem' }}>
            <h1 style={{ fontSize: '14pt', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '0.5rem' }}>
              {sector === 'aviation' ? 'Aircraft Accident/Incident Investigation Report' : 'Marine Casualty Investigation Report'}
            </h1>
            <h2 style={{ fontSize: '12pt', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              ICAO Annex 13 / {sector === 'aviation' ? 'ICAO Doc 9756' : 'IMO Casualty Investigation Code'}
            </h2>
            <p style={{ fontSize: '11pt', marginTop: '1rem' }}>
              Investigation Number: <strong>{investigation?.investigation_number}</strong>
            </p>
            {incident && (
              <div style={{ fontSize: '10pt', marginTop: '0.5rem', color: '#333' }}>
                <p>Incident: {incident.incident_number} | {incident.incident_type} | {incident.date_occurred}</p>
                <p>Location: {incident.location}</p>
                {sector === 'aviation' && incident.aircraft_type && <p>Aircraft: {incident.aircraft_type} {incident.aircraft_registration}</p>}
                {sector === 'maritime' && incident.vessel_name && <p>Vessel: {incident.vessel_name} (IMO: {incident.imo_number})</p>}
              </div>
            )}
          </div>

          {/* Print Table of Contents */}
          <div style={{ marginBottom: '2rem', pageBreakAfter: 'always' }}>
            <h2 style={{ fontSize: '14pt', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '2px solid #000', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
              Table of Contents
            </h2>
            {groups.map((g) => (
              <div key={g.group} style={{ marginBottom: '0.75rem' }}>
                <p style={{ fontSize: '12pt', fontWeight: 'bold', marginBottom: '0.25rem' }}>{g.group}</p>
                {g.keys.map((key) => {
                  const label = SECTION_LABELS[key] || key;
                  const hasContent = sections[key] && (typeof sections[key] === 'string' ? sections[key].trim() : Array.isArray(sections[key]) ? sections[key].filter(i => i?.trim()).length > 0 : true);
                  return (
                    <p key={key} style={{ fontSize: '10pt', paddingLeft: '1.5rem', marginBottom: '0.15rem', color: hasContent ? '#000' : '#999' }}>
                      {label}
                    </p>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Report sections */}
          {groups.map((g) => (
            <div key={g.group} style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '13pt', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '1px solid #666', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>
                {g.group}
              </h2>
              {g.keys.map((sectionKey) => {
                const label = SECTION_LABELS[sectionKey] || sectionKey;
                const value = sections[sectionKey];
                const isList = isListSection(sectionKey);
                const hasContent = value && (typeof value === 'string' ? value.trim() : Array.isArray(value) ? value.filter(i => i?.trim()).length > 0 : true);

                return (
                  <div key={sectionKey} style={{ marginBottom: '1rem', pageBreakInside: 'avoid' }}>
                    <h3 style={{ fontSize: '11pt', fontWeight: 'bold', marginBottom: '0.25rem' }}>{label}</h3>
                    {hasContent ? (
                      isList && Array.isArray(value) ? (
                        <ol style={{ paddingLeft: '1.5rem', margin: 0 }}>
                          {value.filter(v => v?.trim()).map((item, i) => (
                            <li key={`print-${i}`} style={{ fontSize: '11pt', marginBottom: '0.25rem' }}>{item}</li>
                          ))}
                        </ol>
                      ) : (
                        <p style={{ fontSize: '11pt', whiteSpace: 'pre-wrap', margin: 0 }}>{String(value)}</p>
                      )
                    ) : (
                      <p style={{ fontSize: '10pt', color: '#999', fontStyle: 'italic' }}>Not yet documented.</p>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Footer */}
          <div style={{ marginTop: '3rem', borderTop: '1px solid #666', paddingTop: '0.5rem', fontSize: '9pt', color: '#666', textAlign: 'center' }}>
            <p>Generated by RootTrace - Root Cause Intelligence Platform | {new Date().toLocaleDateString()} | {investigation?.investigation_number}</p>
            {reportStatus === 'final' ? <p style={{ fontWeight: 'bold', color: '#000' }}>FINAL REPORT</p> : <p>DRAFT - NOT FOR DISTRIBUTION</p>}
          </div>
        </div>
      </div>
    </>
  );
}
